// On-screen controller for touch screens (Wunsch User 2026-10-26): a stick under the left thumb,
// fire and power-up buttons on the right. Shown once a finger touches the screen without a real
// controller, gone again on a key or controller press; online only, while one's own tank plays.
import type { TankInput } from './input.js';
import { STANDARD_KEYS, drawBubble } from './emotes.js';

/** Called with the emote picked on the touch controller (💬). */
let emoteListener: (index: number) => void = () => {};

export function onTouchEmote(fn: (index: number) => void): void {
  emoteListener = fn;
}

/** The pictures of the four keys' emotes in the 💬 picker. */
let pickerCanvases: HTMLCanvasElement[] = [];
let pickerKeys = STANDARD_KEYS;

/** Shows emotes `ids` (on the keys 1–4) in the picker. */
export function setTouchEmotes(ids: number[]): void {
  pickerKeys = ids;
  pickerCanvases.forEach((canvas, k) => drawPick(canvas, ids[k]));
}

/** The bubble of emote `id`, still, filling `canvas`. */
function drawPick(canvas: HTMLCanvasElement, id: number): void {
  const dpr = window.devicePixelRatio || 1;
  const [w, h] = [96, 40];
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(1.4 * dpr, 0, 0, 1.4 * dpr, (w / 2) * dpr, h * dpr);
  drawBubble(ctx, 0, 0, -12, id, 0.5, 0);
}

/** Stick: radius of its base in CSS px; a direction counts from this share of it (as `STICK` in `input.ts`). */
const STICK_RADIUS = 60;
const STICK_SHARE = 0.5;

let touchMode = false;
let active = false;
let fullscreenAsked = false;
const stick = { id: -1, x: 0, y: 0, dx: 0, dy: 0 };
const held = { fire: -1, item: -1 };
/** The stick's zone (under the panels) and the buttons (over them: the lobby settings cover them in the warm-up). */
let overlay: HTMLElement[] = [];
let base: HTMLElement;
let knob: HTMLElement;

/** The touch mode: on at a touch without a controller, off on a key or controller press. */
export function setTouchMode(on: boolean): void {
  if (touchMode === on) return;
  touchMode = on;
  document.body.classList.toggle('touch', on);
  show();
}

export function isTouchMode(): boolean {
  return touchMode;
}

/** Whether the controller may show: a round runs and one's own tank is in it (set every frame). */
export function setTouchActive(on: boolean): void {
  if (active === on) return;
  active = on;
  show();
}

let right = 0;

/** CSS px kept free at the right edge (the lobby settings in the warm-up): the buttons go left of it. */
export function setTouchRight(px: number): void {
  if (px === right) return;
  right = px;
  document.body.style.setProperty('--touch-right', `${px}px`);
}

function show(): void {
  const on = touchMode && active;
  if (!on) release();
  for (const part of overlay) part.hidden = !on;
}

function release(): void {
  stick.id = held.fire = held.item = -1;
  stick.dx = stick.dy = 0;
  if (base) base.hidden = true;
  for (const part of overlay) part.querySelectorAll('.on').forEach((b) => b.classList.remove('on'));
}

/** Fullscreen once, at the first touch on the controller in a match: the browser bars go too. */
function askFullscreen(): void {
  if (fullscreenAsked || !document.body.classList.contains('fill')) return;
  fullscreenAsked = true;
  // Older Safari has only the prefixed call, without a promise; nothing of it may stop the touch.
  const root = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => void };
  try {
    if (root.requestFullscreen) root.requestFullscreen().catch(() => {});
    else root.webkitRequestFullscreen?.();
  } catch {
    // no fullscreen then
  }
}

// Touch events, not pointer events: only `preventDefault` on a non-passive `touchstart` keeps
// Safari on the iPad from taking the touch for its own gestures (it knows no `touch-action: none`;
// Bug User 2026-10-26: buttons did nothing, a double tap zoomed the page).
const ACTIVE = { passive: false } as const;

/** The touch of `e` with identifier `id` among those that changed, if any. */
const changed = (e: TouchEvent, id: number) => Array.from(e.changedTouches).find((t) => t.identifier === id);

export function listenTouch(): void {
  window.addEventListener(
    'touchstart',
    () => {
      const pads = 'getGamepads' in navigator ? navigator.getGamepads().some((p) => p?.connected) : false;
      if (!pads) setTouchMode(true);
    },
    { capture: true, passive: true },
  );
  const zone = document.createElement('div');
  zone.id = 'touch-stick';
  zone.hidden = true;
  base = document.createElement('div');
  base.className = 'base';
  base.hidden = true;
  knob = document.createElement('div');
  knob.className = 'knob';
  base.append(knob);
  zone.append(base);
  const button = (name: 'fire' | 'item', label: string) => {
    const b = document.createElement('div');
    b.className = `button ${name}`;
    b.textContent = label;
    b.addEventListener(
      'touchstart',
      (e) => {
        e.preventDefault();
        if (held[name] < 0) held[name] = e.changedTouches[0].identifier;
        b.classList.add('on');
        askFullscreen();
      },
      ACTIVE,
    );
    const up = (e: TouchEvent) => {
      e.preventDefault();
      if (!changed(e, held[name])) return;
      held[name] = -1;
      b.classList.remove('on');
    };
    b.addEventListener('touchmove', (e) => e.preventDefault(), ACTIVE);
    b.addEventListener('touchend', up, ACTIVE);
    b.addEventListener('touchcancel', up, ACTIVE);
    return b;
  };
  zone.addEventListener(
    'touchstart',
    (e) => {
      e.preventDefault();
      if (stick.id >= 0) return;
      const t = e.changedTouches[0];
      Object.assign(stick, { id: t.identifier, x: t.clientX, y: t.clientY, dx: 0, dy: 0 });
      base.hidden = false;
      base.style.left = `${t.clientX}px`;
      base.style.top = `${t.clientY}px`;
      knob.style.transform = '';
      askFullscreen();
    },
    ACTIVE,
  );
  zone.addEventListener(
    'touchmove',
    (e) => {
      e.preventDefault();
      const t = changed(e, stick.id);
      if (!t) return;
      let [dx, dy] = [t.clientX - stick.x, t.clientY - stick.y];
      const d = Math.hypot(dx, dy);
      if (d > STICK_RADIUS) [dx, dy] = [(dx / d) * STICK_RADIUS, (dy / d) * STICK_RADIUS];
      [stick.dx, stick.dy] = [dx / STICK_RADIUS, dy / STICK_RADIUS];
      knob.style.transform = `translate(${dx}px, ${dy}px)`;
    },
    ACTIVE,
  );
  const up = (e: TouchEvent) => {
    e.preventDefault();
    if (!changed(e, stick.id)) return;
    stick.id = -1;
    stick.dx = stick.dy = 0;
    base.hidden = true;
  };
  zone.addEventListener('touchend', up, ACTIVE);
  zone.addEventListener('touchcancel', up, ACTIVE);
  const buttons = document.createElement('div');
  buttons.id = 'touch-buttons';
  buttons.hidden = true;
  // 💬 opens the emotes to pick from (Design §11.4); a pick sends it and closes them.
  const picker = document.createElement('div');
  picker.className = 'picker';
  picker.hidden = true;
  const tap = (b: HTMLElement, fn: () => void) => {
    b.addEventListener('touchstart', (e) => (e.preventDefault(), fn()), ACTIVE);
    b.addEventListener('touchmove', (e) => e.preventDefault(), ACTIVE);
    b.addEventListener('touchend', (e) => e.preventDefault(), ACTIVE);
  };
  pickerCanvases = pickerKeys.map((id, k) => {
    const say = document.createElement('div');
    say.className = 'button say';
    const canvas = document.createElement('canvas');
    drawPick(canvas, id);
    say.append(canvas);
    tap(say, () => ((picker.hidden = true), emoteListener(k)));
    picker.append(say);
    return canvas;
  });
  const talk = document.createElement('div');
  talk.className = 'button talk';
  talk.textContent = '💬';
  tap(talk, () => (picker.hidden = !picker.hidden));
  buttons.append(picker, talk, button('item', '★'), button('fire', '●'));
  document.getElementById('game')!.after(zone);
  document.body.append(buttons);
  overlay = [zone, buttons];
}

/** The on-screen controller's input (none while it is hidden). */
export function touchInput(): TankInput {
  const dir = (v: number) => (v >= STICK_SHARE ? 1 : v <= -STICK_SHARE ? -1 : 0);
  return { throttle: -dir(stick.dy), turn: dir(stick.dx), fire: held.fire >= 0, item: held.item >= 0 };
}
